import { forwardRef, Fragment } from 'react'
import type { Attributes, ForwardedRef, ReactNode } from 'react'
import styled from 'styled-components'

import type { components } from '../../constants/schema'

import { useNavigate } from '../../hooks/useNavigate'

import { Text, StyledText } from '../text'

import { Color } from '../../theme/color'

export function ArtipioContentsHeader({ children }: { children: any }) {
  return <HeaderContainer>{children}</HeaderContainer>
}

function HeaderTitle({ children }: { children: any }) {
  return (
    <Text size={16} weight={500}>
      {children}
    </Text>
  )
}

function HeaderDate({ children }: { children: any }) {
  return (
    <Text size={12} color="text_secondary">
      {children}
    </Text>
  )
}

export const ArtipioContents = Object.assign(
  forwardRef(
    ({ children, isPadding = true }: { children: any; isPadding?: boolean }, ref: ForwardedRef<HTMLDivElement>) => {
      return (
        <ContentContainer ref={ref} isPadding={isPadding}>
          {children}
        </ContentContainer>
      )
    },
  ),
)

/**
 * Tiptap HTML 내의 이미지 경로에 API 주소를 추가합니다.
 * @param html Tiptap 에디터에서 가져온 HTML 문자열
 * @param baseUrl 추가할 API 기본 주소 (예: 'https://api.example.com/')
 */
export const transformImageSrc = (html: string, baseUrl: string) => {
  if (!html) return ''

  // 1. 가상 DOM 객체 생성
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  // 2. 모든 img 태그 찾기
  const images = doc.querySelectorAll('img')

  images.forEach((img) => {
    const src = img.getAttribute('src')

    // src가 존재하고, http로 시작하지 않으며(외부 링크 제외), base64가 아닌 경우에만 치환
    if (src && !src.startsWith('http') && !src.startsWith('data:')) {
      // 앞에 슬래시(/) 중복 방지 처리 후 결합
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
      const cleanSrc = src.startsWith('/') ? src.substring(1) : src

      img.setAttribute('src', `${cleanBaseUrl}${cleanSrc}`)
    }
  })

  // 3. 변환된 HTML 다시 문자열로 반환
  return doc.body.innerHTML
}

export function ArtipioContentItem({
  type,
  content,
  alt,
  isFirstImg,
  isLastImg,
  prop,
  ...props
}: {
  type: components['schemas']['MarkdownContent']['type']
  content: components['schemas']['MarkdownContent']['content'] | 'header'
  prop?: components['schemas']['MarkdownContent']['prop']
  alt?: string
  isFirstImg?: boolean
  isLastImg?: boolean
} & Attributes) {
  const navigate = useNavigate()

  const item = () => {
    switch (type) {
      case 'header':
      case 'heading1':
        return (
          <Heading1
            size={24}
            weight={600}
            lineHeight={34}
            color={prop?.color ?? 'text_primary'}
            $textAlign={prop?.align ?? 'initial'}
          >
            {content}
          </Heading1>
        )
      case 'heading2':
        return (
          <Heading2 size={22} weight={600} color={prop?.color ?? 'text_primary'} $textAlign={prop?.align ?? 'initial'}>
            {content}
          </Heading2>
        )
      case 'heading3':
        return (
          <Heading3 size={18} weight={600} color={prop?.color ?? 'text_primary'} $textAlign={prop?.align ?? 'initial'}>
            {content}
          </Heading3>
        )
      case 'body':
        return (
          <Body
            size={14}
            lineHeight={22}
            color={prop?.color ?? 'text_primary'}
            $textAlign={prop?.align ?? 'initial'}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )
      case 'bullet':
        const splitContent = content.split('\n')
        return (
          <Bullet size={14} lineHeight={22}>
            {splitContent.map((content, index) => (
              <Fragment key={index}>
                <span dangerouslySetInnerHTML={{ __html: content }} />
                <br />
              </Fragment>
            ))}
          </Bullet>
        )
      case 'caption':
        return (
          <Caption
            size={12}
            color={prop?.color ?? 'text_secondary'}
            $textAlign={prop?.align ?? 'initial'}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )
      case 'link':
        return (
          <Link href={prop?.link} target="_blank">
            <Text size={14} color="primary_6">
              {content}
            </Text>
          </Link>
        )
      case 'image':
        return (
          <Image
            src={content}
            alt={alt}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = '/img/thumbnail.svg'
            }}
            isFirstImg={isFirstImg}
            isLastImg={isLastImg}
            padding={prop?.shrinkPadding}
          />
        )
      case 'imageLink':
        const isDomain = ['https', 'http'].includes(prop?.link)

        const ImageLinkWrap = ({ children }: { children: ReactNode }) =>
          isDomain ? (
            <ImageLinkAnchor href={prop?.link} target="_blank" padding={prop?.shrinkPadding}>
              {children}
            </ImageLinkAnchor>
          ) : (
            <ImageLinkInApp padding={prop?.shrinkPadding} onClick={() => navigate(prop?.link)}>
              {children}
            </ImageLinkInApp>
          )

        return (
          <ImageLinkWrap>
            <img
              src={content}
              alt={alt}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = '/img/thumbnail.svg'
              }}
            />
          </ImageLinkWrap>
        )
      case 'html':
        const decodeBase64 = (encodedStr: string): string => {
          return decodeURIComponent(
            Array.prototype.map
              .call(atob(encodedStr), (c: string) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
              })
              .join(''),
          )
        }
        const decodeContent = decodeBase64(content)
        return (
          <HtmlContent
            dangerouslySetInnerHTML={{
              __html: transformImageSrc(decodeContent, process.env.REACT_APP_STO_STATIC_URL ?? ''),
            }}
          />
        )
      default:
        return null
    }
  }

  return <CategoryWrap {...props}>{item()}</CategoryWrap>
}

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 24px;
  padding: 8px 0 16px;
  border-bottom: 1px solid ${Color.line};
`

const ContentContainer = styled.div<{ isPadding?: boolean }>`
  ${({ isPadding }) => (isPadding ? 'padding: 0 24px' : undefined)};
`

const CategoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-line;
`

const Heading1 = styled(StyledText)`
  margin: 32px 0 16px;
`

const Heading2 = styled(StyledText)`
  margin: 16px 0 8px;
`

const Heading3 = styled(StyledText)`
  margin: 16px 0 8px;
`

const Body = styled(StyledText)`
  margin: 8px 0;
  > strong {
    font-weight: 700;
  }
`

const Bullet = styled(StyledText)`
  margin: 8px 0;
`

const Caption = styled(StyledText)`
  margin: 8px 0;
`

const Link = styled.a`
  display: inline-block;
  width: fit-content;
`

const Image = styled.img<{ isFirstImg?: boolean; isLastImg?: boolean; padding?: boolean }>`
  max-width: none;
  margin: ${({ isFirstImg }) => (isFirstImg ? 0 : 24)}px 0 ${({ isLastImg }) => (isLastImg ? 0 : 24)}px;
  ${({ padding }) =>
    padding
      ? ''
      : `
        width: calc(100% + 24px * 2);
        margin-left: -24px;
      `}
`

const ImageLinkInApp = styled.div<{ padding?: boolean }>`
  cursor: pointer;
  > img {
    width: 100%;
    max-width: none;
    ${({ padding }) =>
      padding
        ? ''
        : `
        width: calc(100% + 24px * 2);
        margin-left: -24px;
      `}
  }
`

const ImageLinkAnchor = styled.a<{ padding?: boolean }>`
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  > img {
    width: 100%;
    max-width: none;
    ${({ padding }) =>
      padding
        ? ''
        : `
        width: calc(100% + 24px * 2);
        margin-left: -24px;
      `}
  }
`

const HtmlContent = styled.div`
  font-size: 14px;
  font-family: Pretendard-Regular !important;
  line-height: 1.6;
  color: ${Color.text_primary};
  > * {
    min-height: 16px;
  }
  p {
    margin: 0;
  }
  strong {
    font-weight: 600;
  }
  em {
    font-style: italic;
  }
  blockquote {
    margin: 0;
    padding: 4px 8px;
    border-left: 4px solid #dbdbdb;
  }
  a {
    color: ${Color.primary_5};
    text-decoration: underline;
  }
  ul,
  ol {
    list-style-position: inside;
    p {
      display: inline-block;
    }
  }
  ul > li > ul,
  ol > li > ol {
    margin-left: 16px;
  }
  img {
    width: calc(100% + 24px * 2);
    margin-left: -24px;
  }
`

ArtipioContentsHeader.Title = HeaderTitle
ArtipioContentsHeader.Date = HeaderDate
